import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/ui/AdminNavigation';
import MetricsCard from './components/MetricsCard';
import SalesChart from './components/SalesChart';
import QuickActions from './components/QuickActions';
import RecentActivity from './components/RecentActivity';
import AlertCenter from './components/AlertCenter';
import NavigationShortcuts from './components/NavigationShortcuts';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Footer from '../../components/ui/Footer';

const AdminDashboard = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());
  const navigate = useNavigate();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  const handleToggleNav = () => {
    setIsNavCollapsed(!isNavCollapsed);
  };

  const metricsData = [
    {
      title: 'Total Sales',
      value: '$156,900',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      trend: true,
      navigateTo: '/admin/analytics'
    },
    {
      title: 'Active Orders',
      value: '342',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'ShoppingBag',
      trend: true,
      navigateTo: '/admin/orders'
    },
    {
      title: 'Inventory Alerts',
      value: '23',
      change: '+5 new',
      changeType: 'warning',
      icon: 'AlertTriangle',
      trend: false,
      navigateTo: '/admin/products'
    },
    {
      title: 'Vendor Performance',
      value: '94.2%',
      change: '+2.1%',
      changeType: 'positive',
      icon: 'TrendingUp',
      trend: true,
      navigateTo: '/admin/vendors'
    }
  ];

  const handleMetricClick = (navigateTo) => {
    if (navigateTo) {
      navigate(navigateTo);
    }
  };

  const formatDateTime = (date) => {
    return date?.toLocaleString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <div className="flex flex-1">
        <AdminNavigation 
          isCollapsed={isNavCollapsed} 
          onToggleCollapse={handleToggleNav} 
        />

        <div className={`flex-1 flex flex-col transition-all duration-300 ${
          isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        }`}>
          <div className="flex-1">
            {/* Header */}
            <div className="bg-background border-b border-border px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-heading font-semibold text-foreground">
                    Admin Dashboard
                  </h1>
                  <p className="text-sm text-muted-foreground mt-1">
                    {formatDateTime(currentTime)}
                  </p>
                </div>
                
                <div className="flex items-center space-x-4">
                  <Button variant="outline" size="sm" iconName="Download" iconPosition="left">
                    Export Report
                  </Button>
                  <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left">
                    Refresh Data
                  </Button>
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <Icon name="User" size={16} color="white" />
                  </div>
                </div>
              </div>
            </div>

            {/* Dashboard Content */}
            <div className="p-6 space-y-6">
              {/* Metrics Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {metricsData?.map((metric, index) => (
                  <MetricsCard
                    key={index}
                    title={metric?.title}
                    value={metric?.value}
                    change={metric?.change}
                    changeType={metric?.changeType}
                    icon={metric?.icon}
                    trend={metric?.trend}
                    onClick={() => handleMetricClick(metric?.navigateTo)}
                  />
                ))}
              </div>

              {/* Main Dashboard Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left Column - Charts and Analytics */}
                <div className="lg:col-span-2 space-y-6">
                  <SalesChart />
                  <NavigationShortcuts />
                </div>

                {/* Right Column - Actions and Activity */}
                <div className="space-y-6">
                  <QuickActions />
                  <AlertCenter />
                </div>
              </div>

              {/* Bottom Section - Recent Activity */}
              <div className="grid grid-cols-1 xl:grid-cols-1 gap-6">
                <RecentActivity />
              </div>

              {/* System Status Footer */}
              <div className="bg-card rounded-xl border border-border p-4 shadow-elevation-1">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center flex-wrap gap-3 sm:gap-4">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-success rounded-full"></div>
                      <span className="text-sm text-muted-foreground whitespace-nowrap">System Status: Operational</span>
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Last updated: {currentTime?.toLocaleTimeString()}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-xs text-muted-foreground">
                    <span className="whitespace-nowrap">Server Load: 23%</span>
                    <span className="whitespace-nowrap">Database: Healthy</span>
                    <span className="whitespace-nowrap">API Response: 145ms</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <Footer />
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;